import { nextTick } from 'vue'

/**
 * Very small country normalizer.
 * You can extend this mapping as needed.
 */
export function normalizeCountry(raw: string): string | undefined {
  const value = trim(raw).toLowerCase()

  if (!value) return undefined

  if (['nl', 'nederland', 'netherlands', 'the netherlands'].includes(value)) return 'NL'

  if (['be', 'belgie', 'belgië', 'belgium'].includes(value)) return 'BE'

  if (['de', 'duitsland', 'germany', 'bundesrepublik deutschland'].includes(value)) return 'DE'

  if (['fr', 'frankrijk', 'france', 'république française'].includes(value)) return 'FR'

  // Unknown / other
  return value.toUpperCase() // keep something stable, but we don't apply strict rules
}
export function trim(value: string | undefined | null): string {
  return (value ?? '').trim()
}

export function validatePostcodeForCountry(
  rawPostcode: string,
  rawCountry: string
): string | undefined {
  const pc = trim(rawPostcode)
  const norm = normalizeCountry(rawCountry) // can be "NL", "BE", "DE", "FR", or something else

  if (!pc) return 'Postcode is verplicht'

  switch (norm) {
    case 'NL': {
      // 1234 AB
      const value = pc.toUpperCase()
      if (!/^[0-9]{4}\s?[A-Z]{2}$/.test(value)) {
        return 'Postcode is ongeldig voor Nederland (verwacht bijv. 1234 AB)'
      }
      return undefined
    }

    case 'BE': {
      // 1000–9999
      if (!/^[1-9][0-9]{3}$/.test(pc)) {
        return 'Postcode is ongeldig voor België (verwacht bijv. 1000)'
      }
      return undefined
    }

    case 'DE':
    case 'FR': {
      // 5 digits
      if (!/^[0-9]{5}$/.test(pc)) {
        return `Postcode is ongeldig voor ${norm === 'DE' ? 'Duitsland' : 'Frankrijk'} (verwacht 5 cijfers)`
      }
      return undefined
    }

    default:
      // Unknown / other country → only require non-empty
      if (!pc) return 'Postcode is verplicht'
      // you *could* add a generic length rule here if you want
      return undefined
  }
}

export function validateEmailOrUsername(input: string) {
  if (!input) return false
  return !(
    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input) &&
    !/^[a-zA-Z0-9._%+-]+$/.test(input)
  )
}

export async function scrollToTopError() {
  // To allow the errors to be set and the scroll to work
  await nextTick()

  const errors = document.querySelectorAll<HTMLElement>('[data-validation-error="true"]')
  if (!errors.length) return

  const topError = Array.from(errors).reduce((highest, element) => {
    return element.getBoundingClientRect().top < highest.getBoundingClientRect().top
      ? element
      : highest
  })

  const scrollContainer = topError.closest<HTMLElement>('.modal-body')

  if (scrollContainer) {
    const offset = topError.offsetTop - scrollContainer.offsetTop - 24
    scrollContainer.scrollTo({ top: offset, behavior: 'smooth' })
  } else {
    topError.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

export function emptyString(value: string): boolean {
  return value.trim().length === 0
}
